import classNames from 'classnames';
import React, { memo, SyntheticEvent, useCallback } from 'react';
import isFunction from 'lodash/isFunction';
import ReactTooltip from 'react-tooltip';

import { UiSpinner } from 'src/components/UI/Spinner';
import { ChildrenPropType } from 'src/utils/types';

export type UiButtonProps = {
  children: ChildrenPropType;

  type?: 'submit' | 'button' | 'reset';
  className?: string;
  primary?: boolean;
  primaryNew?: boolean;
  secondary?: boolean;
  secondaryNew?: boolean;
  secondaryBordered?: boolean;
  action?: boolean;
  onClick?: (e: SyntheticEvent<HTMLButtonElement | HTMLLinkElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  tag?: 'a' | 'button';
  effects?: boolean;
  spinnerDark?: boolean;
  id?: string;
  notBtn?: boolean; // in case if in must be a button but without its styles
  title?: string;
  tooltipText?: string;
  dataTest?: string;
  dataTestId?: string;
  customClass?: boolean;
  dataAttributes?: Record<string, string | number>;
};

export const UiButton: React.FC<UiButtonProps> = memo(
  ({
    children,
    className,
    onClick,
    type = 'button',
    primary = false,
    primaryNew = false,
    secondary = false,
    secondaryNew = false,
    secondaryBordered = false,
    action = false,
    disabled = false,
    loading = false,
    effects = false,
    tag: Tag = 'button',
    spinnerDark,
    id,
    notBtn,
    title,
    tooltipText,
    dataTest,
    dataTestId,
    customClass,
    dataAttributes = {},
  }) => {
    const preparedOnClick = useCallback(
      (e: SyntheticEvent<HTMLButtonElement | HTMLLinkElement>) => {
        if (!disabled) {
          if (Tag === 'a') {
            /** prevent default action on click on link */
            e.preventDefault();
          }

          if (isFunction(onClick)) {
            onClick(e);
          }
        }
      },
      [Tag, disabled, onClick]
    );

    return (
      <>
        <Tag
          data-tip={tooltipText}
          // eslint-disable-next-line react/button-has-type
          type={type}
          id={id}
          title={title}
          data-test={dataTest}
          data-testid={dataTestId}
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...dataAttributes}
          className={
            classNames(
              className,
              !customClass && {
                btn: Tag !== 'a' && !notBtn,
                'not-btn': notBtn,
                BtnPrimary: primary,
                'btn-primary': primaryNew,
                'btn-secondary': secondaryNew,
                BtnSecondary: secondary,
                BtnSecondaryBordered: secondaryBordered,
                'btn-action': action,
                'btn-loading-spinner disabled': loading,
                disabled,
                'waves-effect waves-light': tooltipText ? false : effects,
                'pointer-events-even-disabled': disabled && tooltipText,
              }
            ) || undefined
          }
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          onClick={preparedOnClick}
          disabled={tooltipText ? false : disabled || loading}
        >
          {loading ? (
            <>
              <div className="loading-spinner-hidden-text">{children}</div>
              <div
                className={classNames('loading-spinner-container text-center', {
                  'spinner-dark': spinnerDark,
                })}
              >
                <UiSpinner />
              </div>
            </>
          ) : (
            children
          )}
        </Tag>

        {tooltipText && <ReactTooltip effect="solid" delayHide={50} className="r-tooltip__tooltip" place="top" />}
      </>
    );
  }
);
